import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {v4 as uuid} from 'uuid';
import { Switch, Route  , Redirect, useLocation } from 'react-router-dom';
import { hasAValue } from '../App';
import TestDriveIntro from './TestDriveIntro';
import TestDriveChoose from './TestDriveChoose';
import TestDriveSetup from './TestDriveSetup';
import TestDriveTry from './TestDriveTry';
import TestDriveRate from './TestDriveRate';
import TestDriveInterpret from './TestDriveInterpret';
import TestDriveReview from './TestDriveReview';
import { TABS, SUBTABS } from './ConstPaths';

const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),
  provider: PropTypes.object,
  isLowTechPath: PropTypes.bool,
  bodySites: PropTypes.arrayOf(PropTypes.string), //from Eval Abilities
  firstMethodCategory: PropTypes.string,
  activeTestDrive: PropTypes.object,

  activeTestDriveId: PropTypes.string,
  activeTestDriveCurrentActivity: PropTypes.string,
  activeTestDriveCurrentActivityName: PropTypes.string,
  activeTestDriveCurrentActivityNumTexts: PropTypes.string,
  activeTestDriveCurrentActivityLanguage: PropTypes.string,
  activeTestDriveCurrentActivityTextUnit: PropTypes.string,
  activeTestDriveCurrentActivityNumTargets: PropTypes.string,
  activeTestDriveCurrentActivityTargetSize: PropTypes.string,
  activeTestDriveCurrentActivityTargetType: PropTypes.string,
  activeTestDriveCurrentActivityNumPrompts: PropTypes.string,
  activeTestDriveCurrentActivityPromptType: PropTypes.string,
  activeTestDriveCurrentActivityNotes: PropTypes.string,
  activeTestDriveCurrentActivityDuration: PropTypes.string,
  activeTestDriveCurrentActivityRawWPM: PropTypes.string,
  activeTestDriveCurrentActivityWPM: PropTypes.string,
  activeTestDriveCurrentActivityTimePerTarget: PropTypes.string,
  activeTestDriveCurrentActivityAccuracy: PropTypes.string,
  activeTestDriveCurrentActivityExtraActivations: PropTypes.string,
  activeTestDriveCurrentActivityDifficulty: PropTypes.string,
  activeTestDriveCurrentActivityComponentStats: PropTypes.arrayOf(PropTypes.object),
  activeTestDriveActivityId: PropTypes.string,
  activeTestDriveActivities: PropTypes.arrayOf(PropTypes.object),

  saveAndResetTestDrive: PropTypes.func.isRequired,
  updateSession: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  provider: null,
  isLowTechPath: false,
  bodySites: [],
  firstMethodCategory: '',
  activeTestDrive: {},
  
  activeTestDriveCurrentActivity: '',
  activeTestDriveCurrentActivityName: '',
  activeTestDriveCurrentActivityNumTexts: '',
  activeTestDriveCurrentActivityLanguage: '',
  activeTestDriveCurrentActivityTextEntry: '',
  activeTestDriveCurrentActivityNumTargets: '',
  activeTestDriveCurrentActivityTargetSize: '',
  activeTestDriveCurrentActivityTargetType: '',
  activeTestDriveCurrentActivityNumPrompts: '',
  activeTestDriveCurrentActivityPromptType: '',

  activeTestDriveCurrentActivityNotes: '',
  activeTestDriveCurrentActivityDuration: '',
  activeTestDriveCurrentActivityRawWPM: '',
  activeTestDriveCurrentActivityWPM: '',
  activeTestDriveCurrentActivityTimePerTarget: '',
  activeTestDriveCurrentActivityAccuracy: '',
  activeTestDriveCurrentActivityExtraActivations: '',
  activeTestDriveCurrentActivityDifficulty: '',
  activeTestDriveCurrentActivityComponentStats:[],
  activeTestDriveActivities: [],

};

function determineIfActiveTestDrive(id, tdObj={}){
  let isActiveTestDrive = false;
  let valuesArray = Object.values(tdObj);
  let anyTdData = (valuesArray.some(hasAValue));

  const numActivities = tdObj.activeTestDriveActivities ? tdObj.activeTestDriveActivities.length : 0;

  if(id && anyTdData && numActivities >= 1)
    isActiveTestDrive = true;
  return(isActiveTestDrive);
}

//when the url matches '/test-drive' this component renders
function TestDrive(props) {  
  //props for customizing views with client info
  const firstName = props.firstName;
  const pronouns = props.pronouns;
  const provider = props.provider;
  const isLowTechPath = props.isLowTechPath;
  const bodySites = props.bodySites;
  //props for choose section
  const hasCurrentMethod = props.hasCurrentMethod;
  const firstMethodCategory = props.firstMethodCategory;
  const isActiveTestDrive = determineIfActiveTestDrive(props.activeTestDriveId, props.activeTestDrive);
  //props for try section
  const activeTestDriveActivityId = props.activeTestDriveActivityId;
  const activeTestDriveId = props.activeTestDriveId;
  const activeTestDrive = props.activeTestDrive;

  const completedTestDrives = props.completedTestDrives;
  const saveAndResetTestDrive = props.saveAndResetTestDrive;
  const updateSession = props.updateSession;

  //data vars for the TestDriveDevSidebar (just for us to see what's happening)
  const dev = {
    showDevSidebar: true,
    tdId: activeTestDriveId,
    td: activeTestDrive,
    actId: activeTestDriveActivityId,
    acts: activeTestDrive.activeTestDriveActivities,
    ctds: completedTestDrives
  }

  const {search} = useLocation();

  console.log("rendering TestDrive: TD object (experiment):  " + JSON.stringify(activeTestDrive));

  //Allows a new test-drive to be created --
  //If there is no activeTestDriveId, it creates a new one and stores in App.state.
  //Used in Choose, Setup, and Try routes to create new test-drive if you jump into those routes using tab navigation.
  const updateActiveTestDriveId = useCallback(() => {
    if(!activeTestDriveId){
      console.log("^^^^^^^^^^^DEFINING a new activeTestDriveId");
      updateSession('activeTestDriveId', uuid());
    }
  }, [activeTestDriveId, updateSession]);

  //render a route for each sub-tab
  //include all props relevant to that sub-tab for initializing and setting
  return(
    <>
    <Switch>
      <Route path={SUBTABS.TEST_INTRO}>
        <TestDriveIntro 
          firstName={firstName}
          isFirstRouteActive={isActiveTestDrive || !activeTestDriveId}
          dev={dev}
        /> 
      </Route>
      {/* 
      //CHOOSE receives filter fields, 
      //updates the selected access method, which is added to picks
      */}
      <Route path={[SUBTABS.CHOOSE + "/:step", SUBTABS.CHOOSE]}>
        <TestDriveChoose 
          firstName={firstName}
          pronouns={pronouns} 
          isActiveTestDrive={isActiveTestDrive}
          isFirstRouteActive={isActiveTestDrive || !activeTestDriveId}
          isLowTechPath={isLowTechPath}
          hasCurrentMethod={hasCurrentMethod}
          firstMethodCategory={firstMethodCategory}
          updateActiveTestDriveId={updateActiveTestDriveId}
          activeTestDrive={activeTestDrive}
          updateSession={updateSession}
          dev={dev}
        /> 
      </Route>
      {/*
      //SETUP receives the selected access method and related info,
      //updates setup details for the test-drive.
      */}
      <Route path={[SUBTABS.SETUP + "/:step", SUBTABS.SETUP]}>
        <TestDriveSetup 
          firstName={firstName}
          pronouns={pronouns}
          isLowTechPath={isLowTechPath}
          bodySites={bodySites}
          updateActiveTestDriveId={updateActiveTestDriveId}
          activeTestDrive={activeTestDrive}
          updateSession={updateSession}
          dev={dev}
        /> 
      </Route>
      {/*
      //TRY receives the selected setup (what parts of the setup does it actually need?)
      //allows for choice of activity
      //updates the Test-drive with data for each activity: setup, observations, measurements
      */}
      <Route path={[SUBTABS.TRY + "/:step", SUBTABS.TRY]}>
        <TestDriveTry 
          firstName={firstName}
          pronouns={pronouns} 
          provider={provider}
          isLowTechPath={isLowTechPath}
          activeTestDriveActivityId={activeTestDriveActivityId}
          updateActiveTestDriveId={updateActiveTestDriveId}
          activeTestDrive={activeTestDrive}
          updateSession={updateSession}
          dev={dev}
        /> 
      </Route>
      {/*
      //RATE receives the Test-drive data, and updates it with ratings and issues
      */}
      <Route path={[SUBTABS.RATE + "/:step", SUBTABS.RATE]}>
        <TestDriveRate 
          firstName={firstName}
          pronouns={pronouns} 
          isLowTechPath={isLowTechPath}
          activeTestDrive={activeTestDrive}
          updateSession={updateSession}
          dev={dev}
        /> 
      </Route>
      {/*
      //INTERPRET receives the issues noted and at least some aspects of setup
      */}
      <Route path={[SUBTABS.INTERPRET + "/:step", SUBTABS.INTERPRET]}>
        <TestDriveInterpret
          firstName={firstName}
          pronouns={pronouns} 
          activeTestDriveId={activeTestDriveId}
          activeTestDrive={activeTestDrive}
          saveAndResetTestDrive={saveAndResetTestDrive}
          updateSession={updateSession}
          dev={dev}
        /> 
      </Route>
      {/*
      //TEST_REVIEW receives all of the Test-drives
      */}
      <Route path={SUBTABS.TEST_REVIEW}>
        <TestDriveReview 
          firstName={firstName}
          pronouns={pronouns}
          activeTestDriveId={activeTestDriveId}
          completedTestDrives={completedTestDrives} 
          dev={dev}
        /> 
      </Route> 
      {/* if path is exactly '/test-drive', go to 'test-drive/intro' */}
      <Route exact path={TABS.TEST}>
        <Redirect to={SUBTABS.TEST_INTRO + search} />
      </Route>
    </Switch>
    </>
  );
}

TestDrive.propTypes = propTypes;
TestDrive.defaultProps = defaultProps;

export default TestDrive;
